/**
 * PersonaWelcomeBanner Component
 * Shows personalized welcome and recommended actions based on student's motivation
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  X, 
  GraduationCap, 
  Wallet, 
  Globe, 
  TrendingUp,
  Sparkles,
  Search,
  Calculator,
  Award,
  MapPin,
  Briefcase,
  PiggyBank,
  Plane,
  Building2,
  ChevronRight,
  Target,
  BookOpen,
  DollarSign,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// Persona configuration with all personalized content
const PERSONA_CONFIG = {
  'course-known': {
    id: 'course-known',
    name: 'I Know My Course',
    icon: GraduationCap,
    gradient: 'from-ux-cyan to-ux-blue',
    bgGlass: 'bg-ux-cyan/10',
    border: 'border-ux-cyan/30',
    accent: 'text-ux-cyan',
    greeting: "You know what you want to study—let's find where!",
    description: "We'll match you with universities that excel in your chosen field.",
    stats: { value: '500+', label: 'Universities in your field' },
    actions: [
      { 
        icon: Search, 
        title: 'Find Universities', 
        desc: 'Search by your specific course',
        link: '/uac-desk/ai-finder',
        primary: true 
      },
      { 
        icon: Award, 
        title: 'View Rankings', 
        desc: 'Top universities for your course',
        link: '/uac-desk/ai-finder?sort=ranking'
      },
      { 
        icon: Target, 
        title: 'Admission Chances', 
        desc: 'Check your eligibility',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: BookOpen, 
        title: 'Course Details', 
        desc: 'Compare curriculum & modules',
        link: '/uac-desk/ai-finder'
      }
    ],
    aiFinderPreset: {
      sortBy: 'ranking',
      focusMessage: "Let's find the best universities for your course!"
    }
  },
  'budget-clarity': {
    id: 'budget-clarity',
    name: 'I Need Budget Clarity',
    icon: Wallet,
    gradient: 'from-emerald-400 to-teal-500',
    bgGlass: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    accent: 'text-emerald-500',
    greeting: "Let's find quality education within your budget!",
    description: "We'll help you discover affordable options with scholarships and financial aid.",
    stats: { value: 'Up to 60%', label: 'Savings possible' },
    actions: [
      { 
        icon: Calculator, 
        title: 'GPA Calculator', 
        desc: 'Convert your grades',
        link: '/uac-desk/gpa-calculator',
        primary: true 
      },
      { 
        icon: Award, 
        title: 'Scholarship Finder', 
        desc: 'Find funding opportunities',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: PiggyBank, 
        title: 'Affordable Programs', 
        desc: 'Budget-friendly universities',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: DollarSign, 
        title: 'Find Universities', 
        desc: 'Search with budget filters',
        link: '/uac-desk/ai-finder'
      }
    ],
    aiFinderPreset: {
      sortBy: 'cost_low',
      focusMessage: "Let's find affordable programs with scholarship opportunities!"
    }
  },
  'country-help': {
    id: 'country-help',
    name: 'Help Me Choose a Country',
    icon: Globe,
    gradient: 'from-ux-violet to-ux-rose',
    bgGlass: 'bg-ux-violet/10',
    border: 'border-ux-violet/30',
    accent: 'text-ux-violet',
    greeting: "Let's find your perfect study destination!",
    description: "Compare countries side-by-side to find the best fit for your lifestyle and goals.",
    stats: { value: '15+', label: 'Destinations to explore' },
    actions: [
      { 
        icon: Globe, 
        title: 'Find Universities', 
        desc: 'Search by country',
        link: '/uac-desk/ai-finder',
        primary: true 
      },
      { 
        icon: MapPin, 
        title: 'Compare Countries', 
        desc: 'Side-by-side comparison',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: Plane, 
        title: 'Explore Options', 
        desc: 'Universities worldwide',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: Building2, 
        title: 'GPA Calculator', 
        desc: 'Check your eligibility',
        link: '/uac-desk/gpa-calculator'
      }
    ],
    aiFinderPreset: {
      sortBy: 'country',
      focusMessage: "Let's explore universities across different countries!"
    }
  },
  'career-focus': {
    id: 'career-focus',
    name: 'I Care About Career Outcomes',
    icon: TrendingUp,
    gradient: 'from-amber-400 to-orange-500',
    bgGlass: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    accent: 'text-amber-500',
    greeting: "Let's maximize your career ROI!",
    description: "We'll focus on programs with strong placement records and career growth potential.",
    stats: { value: '$85K+', label: 'Average starting salary' },
    actions: [
      { 
        icon: TrendingUp, 
        title: 'Top Placements', 
        desc: 'Universities with best job rates',
        link: '/uac-desk/ai-finder',
        primary: true 
      },
      { 
        icon: Briefcase, 
        title: 'Industry Connect', 
        desc: 'Programs with internships',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: Users, 
        title: 'PR Pathways', 
        desc: 'Countries with PR options',
        link: '/uac-desk/ai-finder'
      },
      { 
        icon: DollarSign, 
        title: 'Salary Explorer', 
        desc: 'Expected earnings by field',
        link: '/uac-desk/ai-finder'
      }
    ],
    aiFinderPreset: {
      sortBy: 'placement',
      focusMessage: "Let's find programs with the best career outcomes!"
    }
  },
  'ai-guided': {
    id: 'ai-guided',
    name: 'AI-Guided Journey',
    icon: Sparkles,
    gradient: 'from-ux-cyan to-ux-violet',
    bgGlass: 'bg-ux-cyan/10',
    border: 'border-ux-cyan/30',
    accent: 'text-ux-cyan',
    greeting: "Let AI guide your study abroad journey!",
    description: "Answer a few questions and we'll create a personalized roadmap for you.",
    stats: { value: '30K+', label: 'Students guided' },
    actions: [
      { 
        icon: Sparkles, 
        title: 'AI University Finder', 
        desc: 'Get personalized matches',
        link: '/uac-desk/ai-finder',
        primary: true 
      },
      { 
        icon: Target, 
        title: 'Fast Track Application', 
        desc: 'Quick profile assessment',
        link: '/uac-desk/fast-track'
      },
      { 
        icon: Calculator, 
        title: 'GPA Calculator', 
        desc: 'Convert your grades',
        link: '/uac-desk/gpa-calculator'
      },
      { 
        icon: BookOpen, 
        title: 'Find Universities', 
        desc: 'Explore all options',
        link: '/uac-desk/ai-finder'
      }
    ],
    aiFinderPreset: {
      sortBy: 'match',
      focusMessage: "Let's discover universities perfect for your profile!"
    }
  }
};

// Default config for users without motivation
const DEFAULT_PERSONA = PERSONA_CONFIG['ai-guided'];

const PersonaWelcomeBanner = ({ 
  userMotivation, 
  userName, 
  isDark = false,
  onDismiss,
  onChangePersona 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Get persona config or default
  const persona = PERSONA_CONFIG[userMotivation] || DEFAULT_PERSONA;
  const IconComponent = persona.icon;
  
  // Check if banner was dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem(`persona_banner_dismissed_${userMotivation}`);
    if (dismissed === 'true') {
      setIsVisible(false);
    }
  }, [userMotivation]);
  
  const handleDismiss = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem(`persona_banner_dismissed_${userMotivation}`, 'true');
      if (onDismiss) onDismiss();
    }, 300);
  };
  
  const handleShowAgain = () => {
    localStorage.removeItem(`persona_banner_dismissed_${userMotivation}`);
    setIsVisible(true);
    setIsAnimating(false);
  };
  
  if (!isVisible) {
    return (
      <button
        onClick={handleShowAgain}
        className={`mb-4 text-sm flex items-center gap-2 ${isDark ? 'text-white/60 hover:text-white' : 'text-ux-charcoal/60 hover:text-ux-charcoal'} transition-colors`}
      >
        <IconComponent className="w-4 h-4" />
        <span>Show personalized recommendations</span>
      </button>
    );
  }
  
  return (
    <div 
      className={`mb-6 sm:mb-8 transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-[-20px]' : 'opacity-100'}`}
      data-testid="persona-welcome-banner"
    >
      {/* Main Banner Card */}
      <Card className={`overflow-hidden border-0 ${isDark ? 'bg-[#233842]' : 'bg-white'} shadow-lg`}>
        <div className={`relative bg-gradient-to-r ${persona.gradient} p-6 sm:p-8`}>
          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          {/* Header Content */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white/80 text-sm font-medium">{persona.name}</span>
                <button 
                  onClick={onChangePersona}
                  className="text-white/60 hover:text-white text-xs underline"
                >
                  Change
                </button>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mt-1">
                {userName ? `${userName}, ` : ''}{persona.greeting}
              </h2>
              <p className="text-white/80 text-sm sm:text-base mt-1 max-w-2xl">
                {persona.description}
              </p>
              
              {/* Stats Badge */}
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                <span className="text-white font-bold">{persona.stats.value}</span>
                <span className="text-white/80 text-sm">{persona.stats.label}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Cards */}
        <CardContent className={`p-4 sm:p-6 ${isDark ? 'bg-[#233842]' : 'bg-white'}`}>
          <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
            RECOMMENDED FOR YOU
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {persona.actions.map((action, index) => {
              const ActionIcon = action.icon;
              return (
                <Link
                  key={index}
                  to={action.link}
                  className={`group relative p-4 rounded-xl transition-all duration-200 hover:scale-[1.02] ${
                    action.primary 
                      ? `bg-gradient-to-br ${persona.gradient} text-white shadow-lg`
                      : isDark 
                        ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                        : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                  }`}
                  data-testid={`persona-action-${index}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    action.primary 
                      ? 'bg-white/20' 
                      : isDark ? persona.bgGlass : 'bg-white'
                  }`}>
                    <ActionIcon className={`w-5 h-5 ${
                      action.primary ? 'text-white' : persona.accent
                    }`} />
                  </div>
                  <h4 className={`font-semibold text-sm mb-1 ${
                    action.primary 
                      ? 'text-white' 
                      : isDark ? 'text-white' : 'text-ux-charcoal'
                  }`}>
                    {action.title}
                  </h4>
                  <p className={`text-xs ${
                    action.primary 
                      ? 'text-white/80' 
                      : isDark ? 'text-white/60' : 'text-ux-charcoal/60'
                  }`}>
                    {action.desc}
                  </p>
                  <ChevronRight className={`absolute top-4 right-4 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
                    action.primary ? 'text-white' : persona.accent
                  }`} />
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Export persona config for use in AI Finder
export { PERSONA_CONFIG, DEFAULT_PERSONA };
export default PersonaWelcomeBanner;
